import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider, useToast } from './context/ToastContext';
import ToastContainer from './components/Toast/ToastContainer';
import MainLayout from './layouts/MainLayout';
import Login from './pages/Login/Login';
import VerifyOTP from './pages/Login/VerifyOTP';

// Admin Pages
import AdminDashboard from './pages/Admin/Dashboard/Dashboard';
import AdminOrders from './pages/Admin/Orders/Orders';
import AdminUsers from './pages/Admin/Users/Users';
import AdminPumps from './pages/Admin/Pumps/Pumps';
import AdminManagers from './pages/Admin/Managers/Managers';
import AdminAgents from './pages/Admin/Agents/Agents';
import AdminSettlements from './pages/Admin/Settlements/Settlements';
import AdminSettings from './pages/Admin/Settings/Settings';

// Manager Pages
import ManagerDashboard from './pages/Manager/Dashboard/Dashboard';
import ManagerOrders from './pages/Manager/Orders/Orders';
import ManagerEarnings from './pages/Manager/Earnings/Earnings';
import ManagerProfile from './pages/Manager/Profile/Profile';

// Agent Pages
import AgentDashboard from './pages/Agent/Dashboard/Dashboard';
import AgentReferrals from './pages/Agent/Referrals/Referrals';
import AgentPoints from './pages/Agent/Points/Points';
import AgentProfile from './pages/Agent/Profile/Profile';

import './App.css';
import AdminCMS from './pages/Admin/AdminCMS/AdminCMS';
import PrivacyPolicy from './pages/Admin/CMSDetails/PrivacyPolicy';
import TermsConditions from './pages/Admin/CMSDetails/TermsConditions';
import AboutUs from './pages/Admin/CMSDetails/AboutUs';
import FAQ from './pages/Admin/CMSDetails/FAQ';
import DeleteAccount from './pages/Admin/CMSDetails/DeleteAccount';


// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
    const { authUser, isAuthenticated } = useAuth();
    console.log("allowedRoles", allowedRoles, "authUser", authUser, "isAuthenticated", isAuthenticated, "<><><><><>><<><><><><><<><><<>><><><");
    const location = useLocation();

    // If not authenticated, redirect to login
    if (!isAuthenticated || !authUser) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // If user's role doesn't match allowed roles, redirect to their dashboard
    if (!allowedRoles.includes(authUser.user_type)) {
        const dashboardPath = `/${authUser.user_type}/dashboard`;
        return <Navigate to={dashboardPath} replace />;
    }

    return <MainLayout>{children}</MainLayout>;
};

// Public Route Component (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
    const { authUser, isAuthenticated } = useAuth();

    if (isAuthenticated && authUser) {
        const dashboardPath = `/${authUser.user_type}/dashboard`;
        return <Navigate to={dashboardPath} replace />;
    }

    return children;
};

// App Routes Component
const AppRoutes = () => {
    const { toasts, removeToast } = useToast();

    return (
      <>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <PublicRoute>
                <VerifyOTP />
              </PublicRoute>
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <PublicRoute>
                <PrivacyPolicy />
              </PublicRoute>
            }
          />


          <Route
            path="/terms-conditions"
            element={
              <PublicRoute>
                <TermsConditions />
              </PublicRoute>
            }
          />

          <Route
            path="/about-us"
            element={
              <PublicRoute>
                <AboutUs />
              </PublicRoute>
            }
          />

          <Route
            path="/faq"
            element={
              <PublicRoute>
                <FAQ />
              </PublicRoute>
            }
          />

            <Route
            path="/delete-account"
            element={
              <PublicRoute>
                <DeleteAccount />
              </PublicRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/orders"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pumps"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminPumps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/managers"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminManagers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminAgents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settlements"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminSettlements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/cms"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminCMS />
              </ProtectedRoute>
            }
          />

          {/* Manager Routes */}
          <Route
            path="/manager/dashboard"
            element={
              <ProtectedRoute allowedRoles={["manager"]}>
                <ManagerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/orders"
            element={
              <ProtectedRoute allowedRoles={["manager"]}>
                <ManagerOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/earnings"
            element={
              <ProtectedRoute allowedRoles={["manager"]}>
                <ManagerEarnings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager/profile"
            element={
              <ProtectedRoute allowedRoles={["manager"]}>
                <ManagerProfile />
              </ProtectedRoute>
            }
          />

          {/* Agent Routes */}
          <Route
            path="/agent/dashboard"
            element={
              <ProtectedRoute allowedRoles={["agent"]}>
                <AgentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/referrals"
            element={
              <ProtectedRoute allowedRoles={["agent"]}>
                <AgentReferrals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/points"
            element={
              <ProtectedRoute allowedRoles={["agent"]}>
                <AgentPoints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/agent/profile"
            element={
              <ProtectedRoute allowedRoles={["agent"]}>
                <AgentProfile />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              <Navigate
                to={(() => {
                  // This will be handled by PublicRoute/ProtectedRoute
                  return "/login";
                })()}
                replace
              />
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </>
    );
};

// Main App Component
const App = () => {
    return (
        <AuthProvider>
            <AppProvider>
                <ToastProvider>
                    <Router>
                        <AppRoutes />
                    </Router>
                </ToastProvider>
            </AppProvider>
        </AuthProvider>
    );
};

export default App;

